import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import CharacterCard from "@/components/feature/CharacterCard";
import { characters } from "@/mocks/characters";

const tabs = ["全部", "主角", "配角", "反派"] as const;
const INITIAL_COUNT = 6;
const STEP = 6;

export default function Characters() {
  const [searchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") ?? "";

  const [tab, setTab] = useState<(typeof tabs)[number]>("全部");
  const [keyword, setKeyword] = useState(urlKeyword);
  const [visible, setVisible] = useState(INITIAL_COUNT);

  useEffect(() => {
    setKeyword(urlKeyword);
    setVisible(INITIAL_COUNT);
  }, [urlKeyword]);

  const filtered = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    return characters
      .filter((character) => (tab === "全部" ? true : character.role === tab))
      .filter(
        (character) =>
          !term ||
          character.name.toLowerCase().includes(term) ||
          character.tagline.toLowerCase().includes(term) ||
          character.description.toLowerCase().includes(term)
      )
      .sort((a, b) => a.appearanceOrder - b.appearanceOrder);
  }, [tab, keyword]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;
  const popular = characters.slice(0, 3);

  return (
    <div className="space-y-6">
      <title>甜蜜惩罚角色介绍大全 - 甜蜜惩罚动漫</title>
      <meta
        name="description"
        content="甜蜜惩罚角色介绍大全，收录苏晚、顾时衍等九位主要角色的立绘、定位、性格分析与出场集数，支持主角、配角、反派分类筛选。"
      />
      <meta name="keywords" content="甜蜜惩罚角色,甜蜜惩罚人物介绍,甜蜜惩罚主角,甜蜜惩罚反派" />
      <link rel="canonical" href="https://jdbykj.com/characters" />

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "角色介绍" }]} />

      <header className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
        <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">角色介绍</h1>
        <p className="text-sm md:text-base text-foreground-600 leading-loose font-label max-w-3xl">
          人物是理解《甜蜜惩罚》的第一把钥匙。本页收录九位主要角色的完整档案，
          涵盖角色定位、性格分析与出场集数。你可以按主角、配角、反派切换分类，
          也可以输入姓名快速定位某个角色。
        </p>
      </header>

      <section className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className="flex items-center gap-1.5 p-1 rounded-full bg-background-100 overflow-x-auto no-scrollbar">
            {tabs.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setTab(item);
                  setVisible(INITIAL_COUNT);
                }}
                className={`px-4 py-1.5 rounded-full text-sm font-label whitespace-nowrap transition-colors cursor-pointer ${
                  tab === item ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="relative w-full md:max-w-xs">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => {
                setKeyword(event.target.value);
                setVisible(INITIAL_COUNT);
              }}
              placeholder="搜索角色姓名或标签…"
              className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 font-label focus:outline-none focus:border-primary-400"
            />
          </div>
        </div>
        <p className="text-xs text-foreground-500 font-label mt-3">
          当前分类共 <strong className="text-primary-700">{filtered.length}</strong> 位角色，按出场顺序排列
        </p>
      </section>

      {shown.length === 0 ? (
        <div className="rounded-lg bg-background-50 border border-background-200 p-10 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400 mb-4">
            <i className="ri-emotion-sad-line"></i>
          </span>
          <h2 className="font-heading text-lg text-foreground-950 mb-2">没有找到匹配的角色</h2>
          <p className="text-sm text-foreground-500 font-label mb-5">
            换个关键词试试，或直接看看大家最常浏览的角色。
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {popular.map((character) => (
              <Link
                key={character.id}
                to={`/character/${character.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-900 text-sm font-label hover:bg-accent-200 transition-colors cursor-pointer"
              >
                <i className="ri-user-star-line"></i>
                {character.name}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {shown.map((character, index) => (
              <CharacterCard key={character.id} character={character} index={index} />
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + STEP)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50 border border-background-300 text-sm font-label text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                加载更多角色（剩余 {filtered.length - visible} 位）
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}